import { JSX, useCallback, useRef, useState } from 'react'

import * as RadixTooltip from '@radix-ui/react-tooltip'

import { CommandKeyDecoration } from '~/components/command-menu/components/ShortcutDecoration'
import { type TooltipProps } from '~/components/component-library/Tooltip/types'
import { cn } from '~/utils/tailwind-utils'

export { TooltipLabel } from '~/components/component-library/Tooltip/components/TooltipLabel'
export { TooltipList } from '~/components/component-library/Tooltip/components/TooltipList'
export {
  type TooltipLabelProps,
  type TooltipListProps,
  type TooltipProps,
} from '~/components/component-library/Tooltip/types'

export function Tooltip({
  children,
  content,
  position = 'top',
  align = 'center',
  delay = 100,
  isDisabled = false,
  disableHoverableContent = false,
  triggerClassName = 'relative flex',
  style = {},
  zIndex,
  preventCloseOnClick = false,
  contentClassName,
  onMouseEnterContent,
  onMouseLeaveContent,
  onOpenChange,
  shortcut,
}: TooltipProps): JSX.Element {
  // Defer mounting the portal until first hover (perf optimization).
  // Always render the Radix tree so children stay mounted — switching
  // between a plain <div> and the Radix tree on hover remounts children,
  // which re-triggers autoFocus on inputs.
  const [hasInteracted, setHasInteracted] = useState(false)
  const [open, setOpen] = useState(false)
  const isSelectingRef = useRef(false)

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isSelectingRef.current) return
      setOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [onOpenChange],
  )

  const handleContentPointerDown = useCallback((event: React.PointerEvent) => {
    event.stopPropagation()
    isSelectingRef.current = true
    document.addEventListener(
      'pointerup',
      () => {
        isSelectingRef.current = false
      },
      { once: true },
    )
  }, [])

  const handleContentClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
  }, [])

  return (
    <RadixTooltip.Provider
      delayDuration={delay}
      disableHoverableContent={disableHoverableContent}
    >
      <RadixTooltip.Root open={open} onOpenChange={handleOpenChange}>
        <RadixTooltip.Trigger
          asChild
          onClick={(event) => {
            // *** Warning: this prevents click behavior
            // of anything inside the tooltip.
            // use only when tooltip is nested inside of a button.
            if (preventCloseOnClick) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          onPointerDown={(event) => {
            if (preventCloseOnClick) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
        >
          <div
            className={triggerClassName}
            style={{ zIndex, ...style }}
            onMouseEnter={() => {
              if (!hasInteracted) setHasInteracted(true)
            }}
            // Focus counts too, so keyboard users get the tooltip
            onFocus={() => {
              if (!hasInteracted) setHasInteracted(true)
            }}
          >
            {children}
          </div>
        </RadixTooltip.Trigger>
        {hasInteracted && !isDisabled && (
          <RadixTooltip.Portal>
            <RadixTooltip.Content
              className={cn(
                'anchored invisible z-50 max-w-[min(16rem,var(--radix-tooltip-content-available-width))] transform text-xs leading-relaxed transition [overflow-wrap:anywhere] [@media(hover:hover)]:visible',
                'shadow-black/10 dark:shadow-black/80',
                shortcut
                  ? 'flex items-center gap-2 py-1.5 pl-2.5 pr-1.5'
                  : 'flex-col px-2.5 py-1.5',
                contentClassName,
              )}
              sideOffset={8}
              side={position}
              align={align}
              onPointerDownOutside={(event) => {
                // allows to tooltip to stay on content click, found here:
                // https://github.com/radix-ui/primitives/issues/2029
                if (preventCloseOnClick) {
                  event.preventDefault()
                  event.stopPropagation()
                }
              }}
              collisionPadding={12}
              onPointerDown={handleContentPointerDown}
              onClick={handleContentClick}
              onMouseEnter={onMouseEnterContent}
              onMouseLeave={onMouseLeaveContent}
            >
              {content}
              {shortcut && <CommandKeyDecoration shortcut={shortcut} />}
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        )}
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}
